// src/pages/ConstructionSentry.jsx
// Construction Sentry - AI-Powered Construction Site Safety Monitoring
// Edge AI Platform for PPE Detection, Zone Monitoring, OSHA Compliance

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
  getProjects,
  getCameras,
  getZones,
  getWorkers,
  getSafetyEvents,
  getComplianceSummary,
  getProjectDashboard,
} from '../services/construction';
import { sendChatQuery } from '../services/chat';

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM REACT FLOW NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const HAZARD_COLORS = {
  extreme: { bg: 'bg-red-900/40', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500', ring: 'ring-red-500/30' },
  high: { bg: 'bg-orange-900/40', border: 'border-orange-500', text: 'text-orange-400', dot: 'bg-orange-500', ring: 'ring-orange-500/30' },
  medium: { bg: 'bg-amber-900/40', border: 'border-amber-500', text: 'text-amber-400', dot: 'bg-amber-500', ring: 'ring-amber-500/30' },
  low: { bg: 'bg-emerald-900/40', border: 'border-emerald-500', text: 'text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30' },
  elevated: { bg: 'bg-yellow-900/40', border: 'border-yellow-500', text: 'text-yellow-400', dot: 'bg-yellow-500', ring: 'ring-yellow-500/30' },
  standard: { bg: 'bg-blue-900/40', border: 'border-blue-500', text: 'text-blue-400', dot: 'bg-blue-500', ring: 'ring-blue-500/30' },
};

const SEVERITY_COLORS = {
  critical: 'text-red-400 bg-red-500/20 border-red-500/40',
  high: 'text-orange-400 bg-orange-500/20 border-orange-500/40',
  warning: 'text-amber-400 bg-amber-500/20 border-amber-500/40',
  info: 'text-blue-400 bg-blue-500/20 border-blue-500/40',
};

// ── Site Command Node (aggregate overview) ───────────────────────────────────
const SiteCommandNode = ({ data }) => {
  const compColor = (data.complianceScore || 0) >= 90 ? 'text-emerald-400' : (data.complianceScore || 0) >= 70 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="relative">
      <Handle type="source" position={Position.Bottom} className="!bg-amber-500 !w-2 !h-2" />
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500 rounded-xl p-4 min-w-[320px] shadow-lg shadow-amber-500/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-white">{data.projectName || 'Site Command'}</div>
            <div className="text-xs text-amber-400">AI Safety Monitoring</div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400">Live</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-gray-700/50 rounded px-2 py-1.5 text-center">
            <div className="text-gray-400 text-[10px]">Workers</div>
            <div className="text-white font-bold text-sm">{data.headcount || 0}</div>
          </div>
          <div className="bg-gray-700/50 rounded px-2 py-1.5 text-center">
            <div className="text-gray-400 text-[10px]">Compliance</div>
            <div className={`font-bold text-sm ${compColor}`}>{data.complianceScore || 0}%</div>
          </div>
          <div className="bg-gray-700/50 rounded px-2 py-1.5 text-center">
            <div className="text-gray-400 text-[10px]">Violations</div>
            <div className={`font-bold text-sm ${(data.activeViolations || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{data.activeViolations || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Camera Node (enriched with zone + violation info) ────────────────────────
const CameraNode = ({ data }) => {
  const isOnline = data.status === 'online';
  const violations = data._violationCount || 0;
  const hasCritical = data._hasCritical || false;
  const zoneNames = data._zoneNames || [];

  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} className="!bg-amber-500 !w-2 !h-2" />
      <div className={`border rounded-xl p-3 min-w-[240px] shadow-md transition-all ${
        isOnline
          ? violations > 0
            ? `bg-gray-800/90 ${hasCritical ? 'border-red-500/70 shadow-red-500/10' : 'border-orange-500/50 shadow-orange-500/5'}`
            : 'bg-gray-800/90 border-cyan-500/50 shadow-cyan-500/5'
          : 'bg-gray-800/60 border-gray-600 opacity-60'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <svg className={`w-5 h-5 ${isOnline ? 'text-cyan-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium text-white flex-1">{data.camera_name}</span>
          <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
        </div>
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-gray-400">{(data.camera_type || 'usb').toUpperCase()} &middot; {data.target_fps || 4} FPS</span>
          <span className={`font-medium ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>{data.status}</span>
        </div>
        {zoneNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {zoneNames.map((z) => (
              <span key={z.name} className={`text-[10px] px-1.5 py-0.5 rounded border ${(HAZARD_COLORS[z.hazard] || HAZARD_COLORS.standard).bg} ${(HAZARD_COLORS[z.hazard] || HAZARD_COLORS.standard).border} ${(HAZARD_COLORS[z.hazard] || HAZARD_COLORS.standard).text}`}>
                {z.name}
              </span>
            ))}
          </div>
        )}
        {violations > 0 && (
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${hasCritical ? 'bg-red-500/10 border border-red-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
            <svg className={`w-3.5 h-3.5 ${hasCritical ? 'text-red-400' : 'text-orange-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className={`text-xs font-medium ${hasCritical ? 'text-red-400' : 'text-orange-400'}`}>
              {violations} active violation{violations !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
      {violations > 0 && (
        <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shadow-lg ${hasCritical ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}>
          {violations}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { siteCommand: SiteCommandNode, camera: CameraNode };

// ════════════════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const EventRow = ({ event, cameraName, zoneName, compact = false }) => {
  const sevClass = SEVERITY_COLORS[event.severity] || SEVERITY_COLORS.info;
  const ts = event.timestamp?.endsWith('Z') ? event.timestamp : `${event.timestamp}Z`;
  const age = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  const ageStr = age <= 0 ? 'just now' : age < 60 ? `${age}m ago` : `${Math.round(age / 60)}h ago`;
  const confPct = Math.round((event.confidence || 0) * 100);
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors ${compact ? 'text-xs' : 'text-sm'}`}>
      <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${sevClass}`}>
        {event.severity}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{event.detection_class?.replace(/_/g, ' ')}</div>
        <div className="text-gray-400 text-xs truncate">{cameraName} &middot; {zoneName}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-gray-500 text-xs">{ageStr}</div>
        <div className="text-gray-600 text-xs">{confPct}%</div>
      </div>
    </div>
  );
};

// ── Live Video Feed Panel ────────────────────────────────────────────────────
const LiveFeedPanel = ({ streamUrl, snapshotUrl, onClose }) => {
  const [pos, setPos] = useState({ x: null, y: null });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  // ── Live feed strategy ──────────────────────────────────────────────────
  // SWA's linked-backend proxy buffers long-lived MJPEG, so we poll a
  // snapshot endpoint instead. Naive `<img src=...?_=tick>` polling races
  // the browser: rapid src swaps cancel each other's fetches and the
  // displayed image freezes on the first painted frame even though new
  // bytes are arriving over the wire. Sequential fetch → Blob → object
  // URL avoids the race: each frame is fully in memory before we display.
  const [imgSrc, setImgSrc] = useState(null);
  const lastBlobUrl = useRef(null);
  useEffect(() => {
    if (!snapshotUrl) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${snapshotUrl}?_=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        const prev = lastBlobUrl.current;
        lastBlobUrl.current = url;
        setImgSrc(url);
        if (prev) URL.revokeObjectURL(prev);
      } catch (_) {
        // Network error or non-2xx — keep the last frame, retry on next tick.
      } finally {
        if (!cancelled) timer = setTimeout(tick, 100);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (lastBlobUrl.current) URL.revokeObjectURL(lastBlobUrl.current);
      lastBlobUrl.current = null;
    };
  }, [snapshotUrl]);
  const effectiveSrc = snapshotUrl ? imgSrc : streamUrl;

  const handleMouseDown = (e) => {
    if (isFullscreen) return;
    if (e.target.closest('button') || e.target.closest('a')) return;
    offsetRef.current = {
      x: e.clientX - (dragRef.current?.getBoundingClientRect().left || 0),
      y: e.clientY - (dragRef.current?.getBoundingClientRect().top || 0),
    };
    const onMove = (ev) => {
      setPos({ x: ev.clientX - offsetRef.current.x, y: ev.clientY - offsetRef.current.y });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const toggleFullscreen = () => { setIsFullscreen((prev) => !prev); setPos({ x: null, y: null }); };

  const style = isFullscreen
    ? { position: 'fixed', inset: 0, zIndex: 200 }
    : pos.x !== null ? { position: 'fixed', left: pos.x, top: pos.y, bottom: 'auto', right: 'auto' } : {};

  const containerClass = isFullscreen ? 'fixed inset-0 z-[200]' : 'absolute bottom-4 right-4 z-[100] w-[700px]';

  return (
    <div ref={dragRef} className={containerClass} style={!isFullscreen ? style : undefined}>
      <div className={`bg-gray-900 border border-cyan-500/40 overflow-hidden shadow-2xl shadow-cyan-500/10 ${isFullscreen ? 'h-full flex flex-col rounded-none' : 'rounded-xl'}`}>
        <div className={`flex items-center justify-between px-3 py-2 bg-gray-800/90 border-b border-gray-700 select-none ${isFullscreen ? '' : 'cursor-grab active:cursor-grabbing'}`} onMouseDown={handleMouseDown}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-bold text-white tracking-wider">LIVE</span>
            <span className="text-xs text-gray-400">Edge Camera Feed</span>
          </div>
          <div className="flex items-center gap-2">
            {snapshotUrl && (
              <a href={snapshotUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-white transition-colors" title="Snapshot">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </a>
            )}
            <button onClick={toggleFullscreen} className="text-gray-400 hover:text-white transition-colors" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isFullscreen ? "M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" : "M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"} /></svg>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className={`relative w-full bg-gray-800 overflow-hidden flex items-center justify-center ${isFullscreen ? 'flex-1' : 'h-[400px]'}`}>
          <img src={effectiveSrc} alt="Live Camera Feed" className="max-w-full max-h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            onLoad={(e) => { e.target.style.display = 'block'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'none'; }}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500" style={{ display: 'none' }}>
            <svg className="w-12 h-12 mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /></svg>
            <span className="text-sm font-medium">No Signal</span>
            <span className="text-xs text-gray-600 mt-1">Waiting for edge agent stream...</span>
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/60 text-[10px] text-gray-500">
          <span>MJPEG Stream</span>
          <span>YOLOv8n AI Inference</span>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SHIFT BRIEFING MODAL (dark mode)
// ════════════════════════════════════════════════════════════════════════════

const ShiftBriefingModal = ({ isOpen, onClose, briefing, isLoading, stats, onRequestExpanded, expandedBriefing, isLoadingExpanded }) => {
  const [showExpanded, setShowExpanded] = useState(false);
  if (!isOpen) return null;

  const hasCritical = (stats.criticalEvents || 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${hasCritical ? 'from-red-600 to-orange-600' : 'from-amber-600 to-amber-500'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Shift Briefing</h2>
              <p className="text-white/80 text-sm">AI Safety Summary</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className="text-2xl font-bold text-cyan-400">{stats.headcount || 0}</div>
              <div className="text-xs text-gray-400">Workers</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className={`text-2xl font-bold ${(stats.complianceScore || 0) >= 90 ? 'text-emerald-400' : 'text-amber-400'}`}>{stats.complianceScore || 0}%</div>
              <div className="text-xs text-gray-400">Compliance</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className={`text-2xl font-bold ${(stats.violations || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{stats.violations || 0}</div>
              <div className="text-xs text-gray-400">Violations</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center border border-gray-700">
              <div className={`text-2xl font-bold ${stats.camerasOnline === stats.camerasTotal ? 'text-emerald-400' : 'text-orange-400'}`}>{stats.camerasOnline || 0}/{stats.camerasTotal || 0}</div>
              <div className="text-xs text-gray-400">Cameras</div>
            </div>
          </div>

          {/* AI Briefing */}
          <div className={`rounded-xl p-4 ${hasCritical ? 'bg-red-500/10 border border-red-500/30' : 'bg-gray-800 border border-gray-700'}`}>
            {isLoading ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                <span className="text-gray-400 text-sm">AI Sentry is analyzing site conditions...</span>
              </div>
            ) : briefing ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span className="text-sm font-semibold text-amber-400">AI Analysis</span>
                </div>
                <p className="text-gray-200 text-sm leading-relaxed">{briefing}</p>
              </>
            ) : (
              <p className="text-gray-400 text-sm">
                {hasCritical
                  ? `Warning: ${stats.criticalEvents} critical violation${stats.criticalEvents > 1 ? 's' : ''} detected. Review the safety events panel.`
                  : `Site operating normally. ${stats.violations || 0} active violations across ${stats.camerasTotal || 0} cameras.`
                }
              </p>
            )}
          </div>

          {/* Expanded Analysis */}
          {briefing && (
            <div>
              {!showExpanded ? (
                <button
                  onClick={() => { setShowExpanded(true); if (!expandedBriefing) onRequestExpanded(); }}
                  className="w-full py-2 text-sm text-amber-400 hover:text-amber-300 flex items-center justify-center gap-2 bg-gray-800 rounded-lg hover:bg-gray-800/80 transition-colors border border-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show Detailed Analysis
                </button>
              ) : (
                <div className="bg-gray-800 rounded-xl p-4 border border-amber-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-amber-400">Detailed Analysis</span>
                    <button onClick={() => setShowExpanded(false)} className="text-gray-500 hover:text-gray-300 text-xs">Collapse</button>
                  </div>
                  {isLoadingExpanded ? (
                    <div className="flex items-center gap-3 py-4">
                      <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                      <span className="text-gray-400 text-sm">Generating detailed analysis...</span>
                    </div>
                  ) : expandedBriefing ? (
                    <div className="max-h-72 overflow-y-auto pr-2 text-sm">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({children}) => <h1 className="text-base font-bold text-white mb-2 mt-3 first:mt-0">{children}</h1>,
                          h2: ({children}) => <h2 className="text-sm font-bold text-amber-400 mb-2 mt-3 first:mt-0">{children}</h2>,
                          h3: ({children}) => <h3 className="text-sm font-semibold text-amber-300 mb-1 mt-2">{children}</h3>,
                          p: ({children}) => <p className="text-gray-300 text-sm mb-2 leading-relaxed">{children}</p>,
                          strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                          ul: ({children}) => <ul className="list-disc list-inside text-gray-300 text-sm mb-2 space-y-1 ml-2">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal list-inside text-gray-300 text-sm mb-2 space-y-1 ml-2">{children}</ol>,
                          li: ({children}) => <li className="text-gray-300">{children}</li>,
                          code: ({children}) => <code className="bg-gray-700 px-1 py-0.5 rounded text-amber-300 text-xs font-mono">{children}</code>,
                          table: ({children}) => <div className="overflow-x-auto mb-3 rounded-lg border border-gray-700"><table className="min-w-full text-xs">{children}</table></div>,
                          thead: ({children}) => <thead className="bg-gray-800">{children}</thead>,
                          th: ({children}) => <th className="px-2 py-1.5 text-left font-semibold text-amber-400 whitespace-nowrap">{children}</th>,
                          td: ({children}) => <td className="px-2 py-1.5 text-gray-300 whitespace-nowrap">{children}</td>,
                        }}
                      >
                        {expandedBriefing}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Unable to generate expanded analysis.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-800/50 border-t border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-600 rounded-md flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-xs text-gray-500">Powered by <span className="text-amber-400 font-semibold">AI Sentry</span></span>
          </div>
          <button onClick={onClose} className={`px-6 py-2 ${hasCritical ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700' : 'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600'} text-white font-semibold rounded-lg transition-all`}>
            {hasCritical ? 'Review Violations' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AI CHAT PANEL (dark mode, construction-aware)
// ════════════════════════════════════════════════════════════════════════════

const CHAT_STORAGE_KEY = 'constructionSentryChatSessions';
const CHAT_CURRENT_KEY = 'constructionSentryCurrentSession';

const createChatSession = (suffix = '') => {
  const ts = Date.now();
  return {
    id: `csentry-${ts}${suffix}`,
    title: `Chat ${new Date(ts).toLocaleDateString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    messages: [],
    createdAt: ts,
  };
};

const ChatPanel = ({ siteContext }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const MIN_INTERVAL = 8000;

  // Load sessions from localStorage
  useEffect(() => {
    let initial = [];
    let currentId = null;
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      const storedCurrent = localStorage.getItem(CHAT_CURRENT_KEY);
      if (stored) initial = JSON.parse(stored);
      if (initial.length > 0) {
        currentId = (storedCurrent && initial.find(s => s.id === storedCurrent)) ? storedCurrent : initial.sort((a, b) => b.createdAt - a.createdAt)[0].id;
      } else {
        const s = createChatSession('-init');
        initial = [s];
        currentId = s.id;
      }
    } catch {
      const s = createChatSession('-fallback');
      initial = [s];
      currentId = s.id;
    }
    setSessions(initial);
    setCurrentSessionId(currentId);
  }, []);

  // Persist to localStorage
  useEffect(() => {
    if (sessions.length > 0 && currentSessionId) {
      const t = setTimeout(() => {
        try {
          localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(sessions));
          localStorage.setItem(CHAT_CURRENT_KEY, currentSessionId);
        } catch { /* ignore */ }
      }, 500);
      return () => clearTimeout(t);
    }
  }, [sessions, currentSessionId]);

  const currentSession = useMemo(() => sessions.find(s => s.id === currentSessionId), [sessions, currentSessionId]);
  const messages = useMemo(() => currentSession?.messages || [], [currentSession]);
  const sortedSessions = useMemo(() => [...sessions].sort((a, b) => b.createdAt - a.createdAt), [sessions]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleNewChat = () => {
    const s = createChatSession();
    setSessions(prev => [s, ...prev]);
    setCurrentSessionId(s.id);
    setInput('');
    setIsDropdownOpen(false);
  };

  const handleSelectSession = (id) => { setCurrentSessionId(id); setInput(''); setIsDropdownOpen(false); };

  const handleDeleteSession = (id, e) => {
    e.stopPropagation();
    setSessions(prev => {
      const remaining = prev.filter(s => s.id !== id);
      if (remaining.length === 0) { const s = createChatSession('-new'); setCurrentSessionId(s.id); return [s]; }
      if (currentSessionId === id) setCurrentSessionId([...remaining].sort((a, b) => b.createdAt - a.createdAt)[0].id);
      return remaining;
    });
  };

  const buildContext = () => {
    if (!siteContext) return '';
    let ctx = `Construction Site State:\n`;
    ctx += `- Project: ${siteContext.projectName || 'Unknown'}\n`;
    ctx += `- Workers on site: ${siteContext.headcount || 0}\n`;
    ctx += `- Compliance score: ${siteContext.complianceScore || 0}%\n`;
    ctx += `- Active violations: ${siteContext.violations || 0} (${siteContext.criticalEvents || 0} critical)\n`;
    ctx += `- Cameras: ${siteContext.camerasOnline || 0}/${siteContext.camerasTotal || 0} online\n`;
    if (siteContext.recentEvents?.length > 0) {
      ctx += `\nRecent Safety Events:\n`;
      siteContext.recentEvents.slice(0, 8).forEach(e => {
        ctx += `- ${e.severity} ${e.detection_class?.replace(/_/g, ' ')} (${Math.round((e.confidence || 0) * 100)}% confidence)\n`;
      });
    }
    return ctx;
  };

  const canSend = () => !isLoading && input.trim() && (Date.now() - lastRequestTime >= MIN_INTERVAL);
  const getWaitTime = () => Math.max(0, Math.ceil((MIN_INTERVAL - (Date.now() - lastRequestTime)) / 1000));

  const handleSend = async () => {
    if (!canSend() || !currentSessionId) return;
    const userMessage = input.trim();
    const session = sessions.find(s => s.id === currentSessionId);
    if (!session) return;

    let newTitle = session.title;
    if (session.messages.length === 0) newTitle = userMessage.length > 20 ? `${userMessage.substring(0, 20)}...` : userMessage;

    const userMsg = { role: 'user', content: userMessage };
    setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, userMsg], title: newTitle } : s));
    setInput('');
    setIsLoading(true);
    setLastRequestTime(Date.now());

    try {
      const contextualQuery = `${buildContext()}\n\nUser question: ${userMessage}`;
      const history = session.messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const response = await sendChatQuery(contextualQuery, history);
      const assistantMsg = { role: 'assistant', content: response.content || 'No response received.' };
      setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, assistantMsg] } : s));
    } catch (error) {
      const errorMsg = { role: 'assistant', content: `Error: ${error.message}` };
      setSessions(prev => prev.map(s => s.id === currentSessionId ? { ...s, messages: [...s.messages, errorMsg] } : s));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleQuickAction = (query) => {
    setInput(query);
    setTimeout(() => {
      const fakeEvent = { key: 'Enter', shiftKey: false, preventDefault: () => {} };
      handleKeyPress(fakeEvent);
    }, 100);
  };

  const handleExportPDF = async () => {
    if (messages.length === 0) return;
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    // Build an off-screen container with light styling for clean PDF output
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-9999px;top:0;width:700px;padding:40px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;';

    // Branded header
    container.innerHTML = `
      <div style="border-bottom:3px solid #d97706;padding-bottom:16px;margin-bottom:24px;">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div>
            <div style="font-size:24px;font-weight:700;color:#1a1a1a;">AI Sentry</div>
            <div style="font-size:12px;color:#6b7280;margin-top:2px;">Construction Safety Intelligence Report</div>
          </div>
          <div style="text-align:right;font-size:11px;color:#6b7280;">
            <div>${dateStr}</div>
            <div>Session: ${currentSession?.title || 'Chat'}</div>
          </div>
        </div>
        ${siteContext ? `
        <div style="margin-top:12px;display:flex;gap:24px;font-size:11px;color:#374151;">
          <span><strong>Project:</strong> ${siteContext.projectName || 'N/A'}</span>
          <span><strong>Compliance:</strong> ${siteContext.complianceScore ?? 'N/A'}%</span>
          <span><strong>Violations:</strong> ${siteContext.violations || 0} (${siteContext.criticalEvents || 0} critical)</span>
          <span><strong>Workers:</strong> ${siteContext.headcount || 0}</span>
        </div>` : ''}
      </div>
    `;

    // Render messages
    messages.forEach(msg => {
      const isUser = msg.role === 'user';
      const bubble = document.createElement('div');
      bubble.style.cssText = `margin-bottom:12px;display:flex;justify-content:${isUser ? 'flex-end' : 'flex-start'};`;
      const inner = document.createElement('div');
      inner.style.cssText = isUser
        ? 'background:#d97706;color:#ffffff;padding:8px 14px;border-radius:12px 12px 4px 12px;max-width:80%;font-size:12px;line-height:1.5;'
        : 'background:#f3f4f6;color:#1a1a1a;padding:10px 14px;border-radius:12px 12px 12px 4px;max-width:85%;font-size:12px;line-height:1.5;border:1px solid #e5e7eb;';
      // For assistant messages, render markdown as simple formatted text
      if (isUser) {
        inner.textContent = msg.content;
      } else {
        // Basic markdown → HTML conversion for PDF (bold, lists, line breaks)
        let html = msg.content
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\n- /g, '<br/>• ').replace(/\n\d+\. /g, (m) => '<br/>' + m.trim() + ' ')
          .replace(/\n/g, '<br/>');
        inner.innerHTML = html;
      }
      bubble.appendChild(inner);
      container.appendChild(bubble);
    });

    // Footer
    const footer = document.createElement('div');
    footer.style.cssText = 'margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#9ca3af;text-align:center;';
    footer.textContent = `Generated by AI Sentry \u2022 ai-sentry.com \u2022 ${dateStr}`;
    container.appendChild(footer);

    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;

      let yOffset = 0;
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, margin - yOffset, usableWidth, imgHeight);
        yOffset += pageHeight - margin * 2;
      }

      const safeName = (currentSession?.title || 'chat').replace(/[^a-zA-Z0-9]/g, '_');
      const fileDate = new Date().toISOString().slice(0, 10);
      pdf.save(`AI-Sentry-Chat_${safeName}_${fileDate}.pdf`);
    } finally {
      document.body.removeChild(container);
    }
  };

  // Minimized state — just a floating button
  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 border border-amber-500/40 rounded-xl shadow-lg hover:bg-gray-700 transition-all"
      >
        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        <span className="text-sm font-medium text-amber-400">Ask AI</span>
      </button>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl flex flex-col" style={{ width: '420px', height: '380px', overflow: 'hidden' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-gray-700 bg-gray-800/80 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <div>
              <div className="text-xs font-semibold text-white">Ask AI Sentry</div>
              <div className="text-[10px] text-gray-500">{sessions.length} chat{sessions.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Session dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-gray-300">
                <span className="truncate max-w-[70px]">{currentSession?.title || 'Chat'}</span>
                <svg className={`w-3 h-3 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                  <button onClick={handleNewChat} className="w-full px-3 py-2 text-left text-xs text-amber-400 font-medium hover:bg-gray-700 flex items-center gap-2 border-b border-gray-700">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    New Chat
                  </button>
                  <div className="max-h-36 overflow-y-auto">
                    {sortedSessions.map(session => (
                      <div key={session.id} onClick={() => handleSelectSession(session.id)} className={`px-3 py-2 text-xs cursor-pointer flex items-center justify-between group ${session.id === currentSessionId ? 'bg-amber-500/10 text-amber-400' : 'text-gray-400 hover:bg-gray-700'}`}>
                        <span className="truncate flex-1 mr-2">{session.title}</span>
                        <button onClick={(e) => handleDeleteSession(session.id, e)} className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-0.5">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={handleExportPDF}
              disabled={messages.length === 0}
              title="Export chat to PDF"
              className={`p-1 transition-colors ${messages.length > 0 ? 'text-gray-400 hover:text-amber-400' : 'text-gray-600 cursor-not-allowed'}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </button>
            <button onClick={() => setMinimized(true)} className="p-1 text-gray-500 hover:text-gray-300 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ minHeight: 0 }}>
        {messages.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500 text-xs mb-3">Ask about safety events, compliance, or site status</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              <button onClick={() => handleQuickAction("Summarize today's safety violations")} className="px-2 py-1 text-[10px] bg-red-500/10 text-red-400 rounded-full hover:bg-red-500/20 border border-red-500/30">
                Today's Violations
              </button>
              <button onClick={() => handleQuickAction("What is our current compliance status?")} className="px-2 py-1 text-[10px] bg-amber-500/10 text-amber-400 rounded-full hover:bg-amber-500/20 border border-amber-500/30">
                Compliance Status
              </button>
              <button onClick={() => handleQuickAction("Which cameras have the most events?")} className="px-2 py-1 text-[10px] bg-cyan-500/10 text-cyan-400 rounded-full hover:bg-cyan-500/20 border border-cyan-500/30">
                Camera Summary
              </button>
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'user' ? (
                <div className="bg-amber-600 text-white px-3 py-2 rounded-xl rounded-br-sm max-w-[85%] text-xs">{msg.content}</div>
              ) : (
                <div className="bg-gray-800 border border-gray-700 px-3 py-2 rounded-xl rounded-bl-sm max-w-[85%]">
                  <div className="text-xs text-gray-300">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({children}) => <p className="mb-1.5 last:mb-0 text-xs text-gray-300">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-3 mb-1.5 space-y-0.5 text-xs text-gray-300">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-3 mb-1.5 space-y-0.5 text-xs text-gray-300">{children}</ol>,
                        li: ({children}) => <li className="text-xs text-gray-300">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-white">{children}</strong>,
                        code: ({children}) => <code className="px-1 py-0.5 bg-gray-700 text-amber-300 rounded text-[10px]">{children}</code>,
                        table: ({children}) => <div className="overflow-x-auto mb-2 rounded border border-gray-700"><table className="min-w-full text-xs">{children}</table></div>,
                        thead: ({children}) => <thead className="bg-gray-700">{children}</thead>,
                        th: ({children}) => <th className="px-2 py-1 text-left font-semibold text-amber-400 text-[10px]">{children}</th>,
                        td: ({children}) => <td className="px-2 py-1 text-gray-300 text-[10px]">{children}</td>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>Thinking...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-3 border-t border-gray-700 bg-gray-800/50 rounded-b-xl">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about site safety..."
            className="flex-1 px-3 py-2 text-xs border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-gray-900 text-white placeholder-gray-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!canSend()}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${canSend() ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
          >
            {isLoading ? '...' : '\u2192'}
          </button>
        </div>
        {lastRequestTime > 0 && getWaitTime() > 0 && (
          <div className="text-[10px] text-orange-400 mt-1 text-center">Wait {getWaitTime()}s</div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const EDGE_STREAM_URL = process.env.REACT_APP_EDGE_STREAM_URL || '';
const API_URL = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL_MS = 600000;

const ConstructionSentry = () => {
  // ── API data state ─────────────────────────────────────────────────────
  const [project, setProject] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [zones, setZones] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [events, setEvents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── UI state ───────────────────────────────────────────────────────────
  const [selectedTab, setSelectedTab] = useState('events');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLiveFeed, setShowLiveFeed] = useState(false);
  const pollRef = useRef(null);

  // ── Briefing state ─────────────────────────────────────────────────────
  const [showBriefing, setShowBriefing] = useState(true);
  const [aiBriefing, setAiBriefing] = useState(null);
  const [isLoadingBriefing, setIsLoadingBriefing] = useState(false);
  const [expandedBriefing, setExpandedBriefing] = useState(null);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);

  // ── Lookup maps for event display ──────────────────────────────────────
  const cameraNameMap = useMemo(() => {
    const map = {};
    cameras.forEach((c) => { map[c.id] = c.camera_name; });
    return map;
  }, [cameras]);

  const zoneNameMap = useMemo(() => {
    const map = {};
    zones.forEach((z) => { map[z.id] = z.zone_name; });
    return map;
  }, [zones]);

  // ── AI Briefing functions ──────────────────────────────────────────────
  const fetchBriefing = useCallback(async () => {
    setIsLoadingBriefing(true);
    try {
      const prompt = `Generate a brief construction site safety briefing (2-3 sentences max).
Start with "Attention needed:" if there are critical safety violations, or "All clear:" if the site is safe.
Focus on the most important safety concern right now. Be direct and actionable.`;
      const response = await sendChatQuery(prompt, []);
      const content = response?.content;
      if (content && !content.includes('encountered an error')) setAiBriefing(content);
    } catch { /* ignore */ }
    setIsLoadingBriefing(false);
  }, []);

  const fetchExpandedBriefing = useCallback(async () => {
    setIsLoadingExpanded(true);
    try {
      const prompt = `Generate a detailed construction site safety analysis report. Include:
1. **Safety Summary**: What PPE violations or safety concerns are active?
2. **Affected Areas**: Which zones or cameras are showing the most violations?
3. **Compliance Status**: How is the site performing against OSHA standards?
4. **Risk Assessment**: What is the current risk level?
5. **Recommended Actions**: What should the site superintendent do right now?

Be specific with detection types, camera names, and zone information from the event data.`;
      const response = await sendChatQuery(prompt, []);
      const content = response?.content;
      if (content && !content.includes('encountered an error')) setExpandedBriefing(content);
    } catch { /* ignore */ }
    setIsLoadingExpanded(false);
  }, []);

  // ── Initial data load ──────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [projectsRes, camerasRes, zonesRes, workersRes] = await Promise.all([
          getProjects({ status: 'active' }),
          getCameras(),
          getZones(),
          getWorkers(),
        ]);

        const projects = projectsRes.data;
        if (projects.length === 0) { setError('No active construction projects found'); setLoading(false); return; }

        const proj = projects[0];
        setProject(proj);
        setCameras(camerasRes.data);
        setZones(zonesRes.data);
        setWorkers(workersRes.data);

        const [dashRes, compRes, eventsRes] = await Promise.all([
          getProjectDashboard(proj.id).catch(() => ({ data: null })),
          getComplianceSummary(proj.id).catch(() => ({ data: null })),
          getSafetyEvents({ project_id: proj.id, limit: 30 }),
        ]);

        setDashboard(dashRes.data);
        setCompliance(compRes.data);
        setEvents(eventsRes.data || []);
        setError(null);
      } catch (err) {
        console.error('Failed to load construction data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Trigger briefing after data loads
  useEffect(() => {
    if (!loading && project && showBriefing && !aiBriefing) fetchBriefing();
  }, [loading, project, showBriefing, aiBriefing, fetchBriefing]);

  // ── Event polling ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!project) return;
    const poll = async () => {
      try {
        const [eventsRes, dashRes] = await Promise.all([
          getSafetyEvents({ project_id: project.id, limit: 30 }),
          getProjectDashboard(project.id).catch(() => ({ data: null })),
        ]);
        setEvents(eventsRes.data || []);
        if (dashRes.data) setDashboard(dashRes.data);
      } catch { /* silent */ }
    };
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [project]);

  // ── Derived stats ──────────────────────────────────────────────────────
  const liveStats = dashboard?.live_stats || {};
  const camerasOnline = liveStats.cameras_online ?? cameras.filter((c) => c.status === 'online').length;
  const camerasTotal = liveStats.cameras_total ?? cameras.length;
  const headcount = liveStats.current_headcount ?? 0;
  const complianceScore = liveStats.compliance_score ?? compliance?.compliance_score ?? 0;
  const newEvents = events.filter((e) => e.status === 'new').length;
  const criticalEvents = events.filter((e) => e.severity === 'critical').length;

  const briefingStats = useMemo(() => ({
    headcount, complianceScore, violations: newEvents, criticalEvents, camerasOnline, camerasTotal,
  }), [headcount, complianceScore, newEvents, criticalEvents, camerasOnline, camerasTotal]);

  const siteContext = useMemo(() => ({
    projectName: project?.project_name,
    headcount, complianceScore, violations: newEvents, criticalEvents, camerasOnline, camerasTotal,
    recentEvents: events.slice(0, 10),
  }), [project, headcount, complianceScore, newEvents, criticalEvents, camerasOnline, camerasTotal, events]);

  // ── Per-camera violation counts ────────────────────────────────────────
  const violationsByCamera = useMemo(() => {
    const counts = {};
    const hasCritical = {};
    events.filter((e) => e.status === 'new' && e.camera_id).forEach((e) => {
      counts[e.camera_id] = (counts[e.camera_id] || 0) + 1;
      if (e.severity === 'critical') hasCritical[e.camera_id] = true;
    });
    return { counts, hasCritical };
  }, [events]);

  const zonesByCamera = useMemo(() => {
    const zoneMap = {};
    zones.forEach((z) => { zoneMap[z.id] = z; });
    const result = {};
    cameras.forEach((cam) => {
      if (cam.assigned_zones?.length > 0) {
        result[cam.id] = cam.assigned_zones
          .filter((zid) => zoneMap[zid])
          .map((zid) => ({ name: zoneMap[zid].zone_name, hazard: zoneMap[zid].hazard_level }));
        const hazardRank = { extreme: 5, high: 4, elevated: 3, medium: 2, low: 1, standard: 0 };
        result[`${cam.id}_highest`] = result[cam.id].reduce((best, z) => (hazardRank[z.hazard] || 0) > (hazardRank[best] || 0) ? z.hazard : best, 'standard');
      }
    });
    return result;
  }, [cameras, zones]);

  // ── Build React Flow Nodes & Edges ─────────────────────────────────────
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes = [];
    const edges = [];
    const canvasWidth = cameras.length > 0 ? Math.max(cameras.length * 280, 900) : 900;

    nodes.push({
      id: 'site-command', type: 'siteCommand',
      position: { x: canvasWidth / 2 - 160, y: 30 },
      data: { projectName: project?.project_name || 'Site Command', headcount, complianceScore, activeViolations: newEvents },
    });

    cameras.forEach((cam, i) => {
      const spacing = canvasWidth / (cameras.length + 1);
      nodes.push({
        id: cam.id, type: 'camera',
        position: { x: spacing * (i + 1) - 120, y: 220 },
        data: {
          ...cam,
          _violationCount: violationsByCamera.counts[cam.id] || 0,
          _hasCritical: violationsByCamera.hasCritical[cam.id] || false,
          _zoneNames: zonesByCamera[cam.id] || [],
          _highestHazard: zonesByCamera[`${cam.id}_highest`] || 'standard',
        },
      });
      edges.push({
        id: `sc-${cam.id}`, source: 'site-command', target: cam.id,
        animated: cam.status === 'online',
        style: { stroke: cam.status === 'online' ? '#06b6d4' : '#4b5563', strokeWidth: 1.5 },
      });
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [cameras, project, headcount, complianceScore, newEvents, violationsByCamera, zonesByCamera]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => { setNodes(initialNodes); setEdges(initialEdges); }, [initialNodes, initialEdges, setNodes, setEdges]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading Construction Sentry...</p>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-950">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-500/50 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-gray-300 text-lg font-medium mb-2">Unable to load project data</p>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-1.5rem)] flex flex-col bg-gray-950 text-white overflow-hidden -mx-1 sm:-mx-2 lg:-mx-3 -my-2 lg:-my-3">
      {/* ── Header Bar ──────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-gray-900/90 border-b border-gray-800 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-base font-bold text-white leading-tight">Construction Sentry</h1>
              <p className="text-[11px] text-gray-400">{project?.project_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-gray-800/80">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-gray-400">Live</span>
            </div>
            <div className="flex items-center gap-3 px-3 py-1 rounded bg-gray-800/80 text-gray-300">
              <span>
                <span className={`font-semibold ${complianceScore >= 90 ? 'text-emerald-400' : complianceScore >= 70 ? 'text-amber-400' : 'text-red-400'}`}>{complianceScore}%</span>
                <span className="text-gray-500 ml-1">compliance</span>
              </span>
              <span className="text-gray-700">|</span>
              <span>
                <span className="font-semibold text-cyan-400">{headcount}</span>
                <span className="text-gray-500 ml-1">workers</span>
              </span>
              <span className="text-gray-700">|</span>
              <span>
                <span className={`font-semibold ${newEvents > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{newEvents}</span>
                <span className="text-gray-500 ml-1">violations</span>
                {criticalEvents > 0 && <span className="text-red-400 ml-1">({criticalEvents} critical)</span>}
              </span>
              <span className="text-gray-700">|</span>
              <span>
                <span className={`font-semibold ${camerasOnline === camerasTotal ? 'text-emerald-400' : 'text-orange-400'}`}>{camerasOnline}/{camerasTotal}</span>
                <span className="text-gray-500 ml-1">cameras</span>
              </span>
            </div>

            {/* Briefing button */}
            <button
              onClick={() => { setShowBriefing(true); if (!aiBriefing) fetchBriefing(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Briefing
            </button>

            <button
              onClick={() => setShowLiveFeed(!showLiveFeed)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showLiveFeed
                  ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                  : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${showLiveFeed ? 'bg-red-500 animate-pulse' : 'bg-cyan-500'}`} />
              {showLiveFeed ? 'LIVE' : 'Video Feed'}
            </button>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden bg-gray-950">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes} fitView fitViewOptions={{ padding: 0.3 }}
            minZoom={0.3} maxZoom={2} proOptions={{ hideAttribution: true }}
          >
            <Background color="#374151" gap={20} size={1} />
            <Controls className="!bg-gray-800 !border-gray-700 !rounded-lg [&>button]:!bg-gray-800 [&>button]:!border-gray-700 [&>button]:!text-gray-400 [&>button:hover]:!bg-gray-700" />
          </ReactFlow>

          {/* Chat Panel — bottom-left of canvas */}
          <div className="absolute bottom-4 left-4 z-30">
            <ChatPanel siteContext={siteContext} />
          </div>
        </div>

        {/* ── Right Sidebar ──────────────────────────────────────────── */}
        {sidebarOpen && (
          <div className="w-[360px] flex-shrink-0 bg-gray-900/80 border-l border-gray-800 flex flex-col overflow-hidden">
            <div className="flex border-b border-gray-800">
              {[
                { key: 'events', label: 'Safety Events', count: newEvents },
                { key: 'workers', label: 'Workers' },
                { key: 'zones', label: 'Zones' },
              ].map((tab) => (
                <button
                  key={tab.key} onClick={() => setSelectedTab(tab.key)}
                  className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors relative ${selectedTab === tab.key ? 'text-amber-400 bg-gray-800/50' : 'text-gray-400 hover:text-white'}`}
                >
                  {tab.label}
                  {tab.count > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400">{tab.count}</span>}
                  {selectedTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-950">
              {selectedTab === 'events' && (
                <>
                  {events.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">No safety events</div>
                  ) : (
                    events.map((evt) => (
                      <EventRow key={evt.id} event={evt} cameraName={cameraNameMap[evt.camera_id] || 'Unknown Camera'} zoneName={evt.zone_id ? (zoneNameMap[evt.zone_id] || 'Unknown Zone') : 'Site-wide'} compact />
                    ))
                  )}
                </>
              )}

              {selectedTab === 'workers' && (
                <>
                  <div className="text-xs text-gray-400 mb-2">
                    {workers.filter((w) => w.shift === 'day').length} day shift &middot;{' '}
                    {workers.filter((w) => w.shift === 'night').length} night shift
                  </div>
                  {workers.map((worker) => (
                    <div key={worker.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: worker.hard_hat_color === 'white' ? '#94a3b8' : (worker.hard_hat_color || '#6b7280') }}>
                        {(worker.worker_name || '').split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">{worker.worker_name}</div>
                        <div className="text-xs text-gray-400 truncate">{worker.trade} &middot; {worker.company}</div>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${worker.shift === 'day' ? 'bg-amber-500/20 text-amber-400' : 'bg-indigo-500/20 text-indigo-400'}`}>{worker.shift}</span>
                    </div>
                  ))}
                </>
              )}

              {selectedTab === 'zones' && (
                <>
                  {zones.map((zone) => {
                    const colors = HAZARD_COLORS[zone.hazard_level] || HAZARD_COLORS.standard;
                    const pct = zone.max_occupancy ? Math.round(((zone.current_count || 0) / zone.max_occupancy) * 100) : 0;
                    return (
                      <div key={zone.id} className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                            <span className="text-sm font-medium text-white">{zone.zone_name}</span>
                          </div>
                          <span className={`text-xs font-semibold uppercase ${colors.text}`}>{zone.hazard_level}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-gray-400">{(zone.zone_type || '').replace(/_/g, ' ')}</span>
                          {zone.max_occupancy && <span className="text-white font-mono">{zone.current_count || 0}/{zone.max_occupancy}</span>}
                        </div>
                        {zone.max_occupancy && (
                          <div className="w-full bg-gray-700 rounded-full h-1 mb-2">
                            <div className={`h-1 rounded-full ${pct > 100 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {zone.required_ppe?.map((ppe) => <span key={ppe} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-300">{ppe}</span>)}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-gray-800 p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">OSHA Job: {project?.osha_job_number || '--'}</span>
                <span className="text-gray-500">{project?.site_address || ''}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Shift Briefing Modal */}
      <ShiftBriefingModal
        isOpen={showBriefing && !loading}
        onClose={() => setShowBriefing(false)}
        briefing={aiBriefing}
        isLoading={isLoadingBriefing}
        stats={briefingStats}
        onRequestExpanded={fetchExpandedBriefing}
        expandedBriefing={expandedBriefing}
        isLoadingExpanded={isLoadingExpanded}
      />

      {/* Live Video Feed overlay */}
      {showLiveFeed && (() => {
        const streamCam = cameras.find((c) => c.status === 'online') || cameras[0];
        const camId = streamCam?.id || '';
        const mjpegUrl = EDGE_STREAM_URL ? `${EDGE_STREAM_URL}/live` : `${API_URL}/construction/stream/mjpeg/${camId}`;
        const snapUrl = EDGE_STREAM_URL ? `${EDGE_STREAM_URL}/snap` : (camId ? `${API_URL}/construction/stream/latest/${camId}` : null);
        return <LiveFeedPanel streamUrl={mjpegUrl} snapshotUrl={snapUrl} onClose={() => setShowLiveFeed(false)} />;
      })()}
    </div>
  );
};

export default ConstructionSentry;
